<template>
  <!-- Shares the bottom-right corner with the support chat bubble (about
       48px wide, sitting ~90px up). Its z-index is raised above anything set
       here, so the widget avoids it instead: the bubble and the pill fit
       below it, and the taller open card sits left of it (mr-16). -->
  <div v-if="isVisible" class="fixed z-50 bottom-4 right-4">
    <!-- Collapsed, no call: a bubble, so the widget does not sit over the
         conversation sidebar (labels, attributes) while nothing is happening. -->
    <button
      v-if="!isExpanded && !isOnCall"
      type="button"
      :title="bubbleTitle"
      :aria-label="bubbleTitle"
      class="relative flex items-center justify-center w-12 h-12 rounded-full shadow-lg border bg-white border-slate-100 text-slate-600 hover:text-woot-500 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300"
      @click="isExpanded = true"
    >
      <fluent-icon icon="call" size="20" />
      <span
        class="absolute top-1 right-1 w-2.5 h-2.5 rounded-full border-2 border-white dark:border-slate-800"
        :class="bubbleDotClass"
      />
    </button>

    <!-- Collapsed, on a call: enough to see and end the call. -->
    <div
      v-else-if="!isExpanded"
      class="flex items-center gap-2 max-w-[20rem] py-1.5 pl-3 pr-1.5 rounded-full shadow-lg border bg-white border-slate-100 dark:bg-slate-800 dark:border-slate-700"
    >
      <span
        class="w-2 h-2 shrink-0 rounded-full"
        :class="isConnected ? 'bg-green-500' : 'bg-amber-400'"
      />
      <p
        class="min-w-0 text-xs font-medium truncate text-slate-800 dark:text-slate-100"
      >
        {{ displayName }}
      </p>
      <span
        class="shrink-0 text-xs tabular-nums text-slate-500 dark:text-slate-400"
      >
        {{ isConnected ? formattedDuration : callStateLabel }}
      </span>
      <woot-button
        size="tiny"
        color-scheme="alert"
        icon="call"
        title="End call"
        @click="onHangup"
      />
      <woot-button
        size="tiny"
        variant="clear"
        color-scheme="secondary"
        icon="chevron-up"
        title="Expand"
        @click="isExpanded = true"
      />
    </div>

    <div
      v-else
      class="w-[20rem] mr-16 rounded-lg shadow-lg border bg-white border-slate-100 dark:bg-slate-800 dark:border-slate-700"
    >
      <!-- Registration failed: the SDK cannot report this itself, so it is
         surfaced prominently rather than leaving a silently dead phone. -->
      <div v-if="hasRegistrationFailed" class="p-4">
        <div class="flex items-start gap-2">
          <fluent-icon
            icon="warning"
            size="16"
            class="mt-0.5 shrink-0 text-red-500"
          />
          <div class="flex-1">
            <p class="text-sm font-medium text-slate-800 dark:text-slate-100">
              Web calling unavailable
            </p>
            <p
              class="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400"
            >
              {{
                errorMessage ||
                'Your softphone could not connect. Check your network, then retry.'
              }}
            </p>
          </div>
          <woot-button
            size="tiny"
            variant="clear"
            color-scheme="secondary"
            icon="chevron-down"
            title="Minimise"
            @click="isExpanded = false"
          />
        </div>
        <woot-button
          size="small"
          variant="clear"
          class="mt-2"
          @click="$emit('retry')"
        >
          Retry
        </woot-button>
      </div>

      <!-- Another tab already owns the softphone. -->
      <div v-else-if="!hasPhoneLease" class="flex items-center gap-2 p-4">
        <fluent-icon icon="info" size="16" class="shrink-0 text-slate-400" />
        <p class="flex-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
          Calls are active in another Chatwoot tab.
        </p>
        <woot-button
          size="tiny"
          variant="clear"
          color-scheme="secondary"
          icon="chevron-down"
          title="Minimise"
          @click="isExpanded = false"
        />
      </div>

      <template v-else>
        <div
          class="flex items-center justify-between px-4 py-2 border-b border-slate-100 dark:border-slate-700"
        >
          <div class="flex items-center gap-2">
            <span
              class="w-2 h-2 rounded-full"
              :class="isRegistered ? 'bg-green-500' : 'bg-slate-300'"
            />
            <span
              class="text-xs font-medium text-slate-600 dark:text-slate-300"
            >
              {{ statusLabel }}
            </span>
          </div>
          <div class="flex items-center gap-1">
            <span
              v-if="isOnCall"
              class="text-xs tabular-nums text-slate-500 dark:text-slate-400"
            >
              {{ formattedDuration }}
            </span>
            <!-- The agent's own switch. Hidden mid-call: unregistering does not
               end a call, and offering it there only invites the confusion. -->
            <woot-button
              v-else
              size="tiny"
              variant="clear"
              :color-scheme="isAvailable ? 'secondary' : 'warning'"
              :title="availabilityHint"
              @click="onToggleAvailability"
            >
              {{ isAvailable ? 'Stop calls' : 'Take calls' }}
            </woot-button>
            <!-- Not offered while ringing, so a call cannot be hidden by accident. -->
            <woot-button
              v-if="!isIncoming"
              size="tiny"
              variant="clear"
              color-scheme="secondary"
              icon="chevron-down"
              title="Minimise"
              @click="isExpanded = false"
            />
          </div>
        </div>

        <div v-if="isOnCall" class="p-4">
          <div class="flex items-center gap-2 min-w-0">
            <thumbnail
              v-if="callerContact"
              :src="callerContact.thumbnail"
              :username="callerContact.name"
              size="32px"
            />
            <div class="min-w-0">
              <div class="flex items-center gap-1.5 min-w-0">
                <p
                  class="text-sm font-medium truncate text-slate-800 dark:text-slate-100"
                >
                  {{ displayName }}
                </p>
                <a
                  v-if="callerContact"
                  :href="contactProfileLink"
                  target="_blank"
                  rel="noopener nofollow noreferrer"
                  title="Open contact"
                  class="shrink-0"
                >
                  <woot-button
                    size="tiny"
                    icon="open"
                    variant="clear"
                    color-scheme="secondary"
                  />
                </a>
              </div>
              <p
                v-if="callerContact"
                class="text-xs truncate text-slate-500 dark:text-slate-400"
              >
                {{ activeCall.remoteNumber }}
              </p>
            </div>
          </div>
          <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
            {{ callStateLabel }}
          </p>

          <!-- Incoming: accept or reject. -->
          <div v-if="isIncoming" class="flex gap-2 mt-4">
            <woot-button
              size="small"
              color-scheme="success"
              icon="call"
              @click="onAccept"
            >
              Accept
            </woot-button>
            <woot-button
              size="small"
              color-scheme="alert"
              icon="call"
              @click="onHangup"
            >
              Reject
            </woot-button>
          </div>

          <!-- In progress: mute, hold, hang up. -->
          <div v-else class="flex gap-2 mt-4">
            <woot-button
              size="small"
              variant="smooth"
              :color-scheme="isMuted ? 'warning' : 'secondary'"
              :disabled="!isConnected"
              @click="onToggleMute"
            >
              {{ isMuted ? 'Unmute' : 'Mute' }}
            </woot-button>
            <woot-button
              size="small"
              variant="smooth"
              :color-scheme="isOnHold ? 'warning' : 'secondary'"
              :disabled="!isConnected"
              @click="onToggleHold"
            >
              {{ isOnHold ? 'Resume' : 'Hold' }}
            </woot-button>
            <woot-button
              size="small"
              variant="smooth"
              :color-scheme="isInCallPadOpen ? 'primary' : 'secondary'"
              icon="keyboard"
              title="Keypad"
              :disabled="!isConnected"
              @click="isInCallPadOpen = !isInCallPadOpen"
            />
            <woot-button
              size="small"
              color-scheme="alert"
              icon="call"
              @click="onHangup"
            >
              End
            </woot-button>
          </div>

          <!-- Tones for IVR menus on the number being called. -->
          <dial-pad
            v-if="isInCallPadOpen && isConnected"
            class="mt-3"
            @press="onSendTone"
          />

          <router-link
            v-if="outboundConversationPath"
            :to="outboundConversationPath"
            class="inline-flex items-center gap-1 mt-3 text-xs font-medium text-woot-500"
          >
            <fluent-icon icon="open" size="12" />
            Open conversation
          </router-link>
        </div>

        <div v-else class="p-4">
          <div class="flex items-center justify-between gap-2">
            <p class="text-xs leading-5 text-slate-500 dark:text-slate-400">
              {{ idleLabel }}
            </p>
            <woot-button
              v-if="canDial"
              size="tiny"
              variant="smooth"
              color-scheme="secondary"
              icon="keyboard"
              @click="isDialPadOpen = !isDialPadOpen"
            >
              {{ isDialPadOpen ? 'Close' : 'Dial' }}
            </woot-button>
          </div>

          <!-- Dial any number. The conversation is created (or reopened) on
             dial, so the call is in the CRM before the customer picks up. -->
          <form
            v-if="canDial && isDialPadOpen"
            class="mt-3"
            @submit.prevent="onDial"
          >
            <!-- Number display. Styled here in full: the global input styles
               skip type="tel", which left it at browser defaults. -->
            <div
              class="relative flex items-center h-12 rounded-lg border transition-colors duration-150 bg-slate-50 dark:bg-slate-900 focus-within:bg-white dark:focus-within:bg-slate-900"
              :class="
                dialError
                  ? 'border-red-400 dark:border-red-500'
                  : 'border-slate-200 dark:border-slate-600 focus-within:border-woot-500 dark:focus-within:border-woot-500'
              "
            >
              <input
                ref="dialInput"
                v-model="dialedNumber"
                type="tel"
                inputmode="tel"
                placeholder="Enter number"
                autocomplete="off"
                aria-label="Phone number"
                class="reset-base w-full h-full px-12 m-0 text-center bg-transparent border-0 rounded-lg outline-none text-lg font-medium tracking-wide tabular-nums text-slate-900 dark:text-slate-50 placeholder:text-sm placeholder:font-normal placeholder:tracking-normal placeholder:text-slate-400 dark:placeholder:text-slate-500"
                @input="dialError = ''"
              />
              <button
                v-if="dialedNumber"
                type="button"
                title="Delete last digit"
                aria-label="Delete last digit"
                class="absolute right-1 flex items-center justify-center w-10 h-10 rounded-md text-slate-500 hover:text-slate-800 hover:bg-slate-100 dark:text-slate-400 dark:hover:text-slate-50 dark:hover:bg-slate-700"
                @click="onBackspace"
              >
                <!-- Backspace glyph, inline: the icon set has none, and a
                   circled X reads as "clear all" rather than one digit. -->
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.75"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="w-[22px] h-[22px] shrink-0"
                  aria-hidden="true"
                >
                  <path
                    d="M9 5h10a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H9l-6-7 6-7Z"
                  />
                  <path d="m12 9.5 5 5m0-5-5 5" />
                </svg>
              </button>
            </div>
            <p
              class="min-h-[1rem] mt-1.5 mb-2 text-xs text-center"
              :class="dialError ? 'text-red-500 dark:text-red-400' : ''"
              role="alert"
            >
              {{ dialError }}
            </p>

            <dial-pad @press="onDialPadPress" />

            <woot-button
              class="w-full justify-center mt-3"
              size="small"
              color-scheme="success"
              icon="call"
              :is-loading="isDialing"
              :disabled="!dialedNumber || isDialing"
            >
              Call
            </woot-button>
          </form>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';
import DialPad from './DialPad.vue';
import { sanitizeDialInput } from 'dashboard/helper/dialNumber';
import {
  REGISTRATION_STATES,
  CALL_STATES,
  CALL_DIRECTIONS,
} from 'dashboard/constants/calling';

export default {
  name: 'CallWidget',

  components: { Thumbnail, DialPad },

  data() {
    return {
      elapsedSeconds: 0,
      durationTimer: null,
      isDialPadOpen: false,
      isInCallPadOpen: false,
      dialedNumber: '',
      dialError: '',
      isDialing: false,
      isExpanded: false,
    };
  },

  computed: {
    ...mapGetters({
      registrationState: 'calling/getRegistrationState',
      callState: 'calling/getCallState',
      activeCall: 'calling/getActiveCall',
      isRegistered: 'calling/isRegistered',
      isOnCall: 'calling/isOnCall',
      isMuted: 'calling/isMuted',
      isOnHold: 'calling/isOnHold',
      hasPhoneLease: 'calling/hasPhoneLease',
      errorMessage: 'calling/getErrorMessage',
      isProvisioned: 'calling/isProvisioned',
      isAvailable: 'calling/isAvailable',
      outboundContext: 'calling/getOutboundContext',
      currentAccountId: 'getCurrentAccountId',
    }),

    // Dialling needs a registered device; an agent who has stopped taking
    // calls must switch back on first, since the dial rings their browser.
    canDial() {
      return this.isRegistered && this.isAvailable;
    },

    outboundConversationPath() {
      const displayId = this.outboundContext?.display_id;
      if (!displayId) return '';
      return `/app/accounts/${this.currentAccountId}/conversations/${displayId}`;
    },

    availabilityHint() {
      return this.isAvailable
        ? 'Stop receiving calls in this browser'
        : 'Start receiving calls again';
    },

    idleLabel() {
      if (!this.isAvailable)
        return 'Not taking calls. Calls will not ring here.';
      return this.isRegistered
        ? 'Ready to take calls.'
        : 'Connecting your softphone…';
    },

    callerContact() {
      return this.activeCall?.contact || null;
    },

    contactProfileLink() {
      if (!this.callerContact) return '';
      return `/app/accounts/${this.currentAccountId}/contacts/${this.callerContact.id}`;
    },

    hasRegistrationFailed() {
      return this.registrationState === REGISTRATION_STATES.FAILED;
    },

    // The widget stays out of the way until it has something to say: a live
    // call, a failure, a tab conflict — or an agent who has signed off, who
    // would otherwise have no way back on.
    isVisible() {
      if (!this.isProvisioned) return false;
      return (
        this.isOnCall ||
        this.hasRegistrationFailed ||
        !this.hasPhoneLease ||
        !this.isAvailable ||
        this.registrationState !== REGISTRATION_STATES.UNREGISTERED
      );
    },

    bubbleTitle() {
      if (this.hasRegistrationFailed) return 'Web calling unavailable';
      if (!this.hasPhoneLease) return 'Calls are active in another tab';
      return this.statusLabel;
    },

    bubbleDotClass() {
      if (this.hasRegistrationFailed) return 'bg-red-500';
      if (!this.hasPhoneLease) return 'bg-slate-300';
      if (!this.isAvailable) return 'bg-amber-400';
      return this.isRegistered ? 'bg-green-500' : 'bg-slate-300';
    },

    isIncoming() {
      return this.callState === CALL_STATES.INCOMING;
    },

    isConnected() {
      return this.callState === CALL_STATES.CONNECTED;
    },

    displayName() {
      const { remoteName, remoteNumber } = this.activeCall || {};
      return (
        this.callerContact?.name ||
        remoteName ||
        remoteNumber ||
        'Unknown number'
      );
    },

    statusLabel() {
      if (!this.isAvailable) return 'Not taking calls';
      if (this.isRegistered) return 'Web calling on';
      if (this.registrationState === REGISTRATION_STATES.REGISTERING)
        return 'Connecting…';
      return 'Web calling off';
    },

    callStateLabel() {
      const isOutbound =
        this.activeCall?.direction === CALL_DIRECTIONS.OUTBOUND;
      if (this.isIncoming) return 'Incoming call';
      if (this.callState === CALL_STATES.OUTGOING) return 'Connecting…';
      if (this.isOnHold) return 'On hold';
      // The browser only sees its own leg; for an outbound call the customer
      // may still be ringing after we are "connected".
      return isOutbound ? 'Calling customer' : 'Inbound call';
    },

    formattedDuration() {
      const minutes = Math.floor(this.elapsedSeconds / 60);
      const seconds = this.elapsedSeconds % 60;
      return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(
        2,
        '0'
      )}`;
    },
  },

  watch: {
    // The SDK reports duration only alongside events, so the ticking clock the
    // agent sees is kept locally and reset whenever a call starts or ends.
    callState(newState, oldState) {
      // Open the full card when a call starts (ringing, or dialled from a
      // contact) and fold back to the bubble when it ends.
      if (oldState === CALL_STATES.IDLE && newState !== CALL_STATES.IDLE) {
        this.isExpanded = true;
      } else if (newState === CALL_STATES.IDLE) {
        this.isExpanded = false;
      }
      if (newState === CALL_STATES.CONNECTED) {
        this.startDurationTimer();
      } else if (
        newState === CALL_STATES.IDLE ||
        oldState === CALL_STATES.IDLE
      ) {
        this.stopDurationTimer();
      }
      if (newState === CALL_STATES.IDLE) this.isInCallPadOpen = false;
    },

    // A dead softphone needs the agent's attention; everything else can wait
    // in the bubble.
    hasRegistrationFailed: {
      immediate: true,
      handler(hasFailed) {
        if (hasFailed) this.isExpanded = true;
      },
    },

    isDialPadOpen(isOpen) {
      if (isOpen) this.$nextTick(() => this.$refs.dialInput?.focus());
    },

    dialedNumber(value) {
      const sanitized = sanitizeDialInput(value);
      if (sanitized !== value) this.dialedNumber = sanitized;
    },
  },

  beforeDestroy() {
    this.stopDurationTimer();
  },

  methods: {
    startDurationTimer() {
      this.stopDurationTimer();
      this.elapsedSeconds = 0;
      this.durationTimer = setInterval(() => {
        this.elapsedSeconds += 1;
      }, 1000);
    },

    stopDurationTimer() {
      if (this.durationTimer) clearInterval(this.durationTimer);
      this.durationTimer = null;
      this.elapsedSeconds = 0;
    },

    onAccept() {
      this.$store.dispatch('calling/acceptCall');
    },

    onHangup() {
      this.$store.dispatch('calling/hangupCall');
    },

    onToggleMute() {
      this.$store.dispatch('calling/toggleMute');
    },

    onToggleHold() {
      this.$store.dispatch('calling/toggleHold');
    },

    onDialPadPress(key) {
      this.dialedNumber += key;
      this.dialError = '';
      this.$refs.dialInput?.focus();
    },

    onBackspace() {
      this.dialedNumber = this.dialedNumber.slice(0, -1);
      this.$refs.dialInput?.focus();
    },

    async onDial() {
      if (!this.dialedNumber || this.isDialing) return;
      this.isDialing = true;
      this.dialError = '';
      try {
        await this.$store.dispatch('calling/dialNumber', this.dialedNumber);
        this.dialedNumber = '';
        this.isDialPadOpen = false;
      } catch (error) {
        this.dialError =
          error?.response?.data?.error ||
          error?.message ||
          'Could not place the call.';
      } finally {
        this.isDialing = false;
      }
    },

    onSendTone(key) {
      this.$store.dispatch('calling/sendDtmf', key);
    },

    onToggleAvailability() {
      this.$store.dispatch('calling/setAvailability', !this.isAvailable);
    },
  },
};
</script>
